import type { WardInfo } from '@/types/ward';

/**
 * Navigation Service
 * Handles external navigation to Google Maps and other map services
 */

export interface NavigationOptions {
  origin?: string | { lat: number; lng: number };
  destination: string | { lat: number; lng: number };
  travelMode?: 'driving' | 'walking' | 'transit' | 'bicycling';
  waypoints?: string[];
  region?: string;
  avoidHighways?: boolean;
  avoidTolls?: boolean;
}

class NavigationService {
  private static instance: NavigationService;
  private readonly GOOGLE_MAPS_BASE_URL = 'https://www.google.com/maps/dir/';
  private readonly REGION = 'vn'; // Vietnam region

  private constructor() {}

  static getInstance(): NavigationService {
    if (!NavigationService.instance) {
      NavigationService.instance = new NavigationService();
    }
    return NavigationService.instance;
  }

  /**
   * Build Google Maps navigation URL
   */
  buildGoogleMapsUrl(options: NavigationOptions): string {
    const params = new URLSearchParams();
    
    // API version
    params.append('api', '1');
    
    // Origin - can be coordinates or address
    if (options.origin) {
      const originStr = this.formatLocation(options.origin);
      params.append('origin', originStr);
    }
    
    // Destination - required
    const destStr = this.formatLocation(options.destination);
    params.append('destination', destStr);
    
    // Travel mode
    if (options.travelMode) {
      params.append('travelmode', options.travelMode);
    }
    
    // Waypoints
    if (options.waypoints && options.waypoints.length > 0) {
      params.append('waypoints', options.waypoints.join('|'));
    }
    
    // Region bias for Vietnam
    params.append('region', options.region || this.REGION);
    
    // Avoid options
    const avoidOptions = [];
    if (options.avoidHighways) avoidOptions.push('highways');
    if (options.avoidTolls) avoidOptions.push('tolls');
    if (avoidOptions.length > 0) {
      params.append('avoid', avoidOptions.join('|'));
    }
    
    return `${this.GOOGLE_MAPS_BASE_URL}?${params.toString()}`;
  }

  /**
   * Navigate to ward centroid
   */
  navigateToWard(ward: WardInfo, options?: Partial<NavigationOptions>): void {
    if (!ward.geometry) {
      console.error('Ward has no geometry');
      return;
    }
    
    // Calculate centroid for polygon
    const centroid = this.calculateCentroid(ward.geometry);
    
    const navOptions: NavigationOptions = {
      destination: centroid,
      travelMode: 'driving',
      ...options,
    };
    
    const url = this.buildGoogleMapsUrl(navOptions);
    this.openNavigation(url);
  }

  /**
   * Navigate from current location to destination
   */
  async navigateFromCurrentLocation(
    destination: string | { lat: number; lng: number },
    options?: Partial<NavigationOptions>
  ): Promise<void> {
    try {
      const position = await this.getCurrentPosition();
      const origin = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      };
      
      const navOptions: NavigationOptions = {
        origin,
        destination,
        travelMode: 'driving',
        ...options,
      };
      
      const url = this.buildGoogleMapsUrl(navOptions);
      this.openNavigation(url);
    } catch (error) {
      console.error('Failed to get current location:', error);
      // Fallback to navigation without origin
      const navOptions: NavigationOptions = {
        destination,
        travelMode: 'driving',
        ...options,
      };
      
      const url = this.buildGoogleMapsUrl(navOptions);
      this.openNavigation(url);
    }
  }

  /**
   * Open navigation in appropriate app/tab
   */
  openNavigation(url: string): void {
    // Detect if mobile and if Google Maps app is available
    if (this.isMobileDevice()) {
      // Try to open in Google Maps app first
      const appUrl = url.replace('https://www.google.com/maps', 'comgooglemaps://');
      
      // Create invisible iframe to try app launch
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = appUrl;
      document.body.appendChild(iframe);
      
      // Fallback to web after timeout
      setTimeout(() => {
        document.body.removeChild(iframe);
        window.open(url, '_blank');
      }, 1000);
    } else {
      // Desktop - open in new tab
      window.open(url, '_blank');
    }
  }

  /**
   * Format location for URL
   */
  private formatLocation(location: string | { lat: number; lng: number }): string {
    if (typeof location === 'string') {
      // return encodeURIComponent(location);
      return location;
    }
    return `${location.lat},${location.lng}`;
  }

  /**
   * Calculate centroid of polygon
   */
  private calculateCentroid(geometry: __esri.Geometry): { lat: number; lng: number } {
    // For ArcGIS polygon geometry
    if ('rings' in geometry && (geometry as any).rings && (geometry as any).rings.length > 0) {
      const ring = (geometry as any).rings[0];
      let sumX = 0;
      let sumY = 0;
      
      for (const point of ring) {
        sumX += point[0];
        sumY += point[1];
      }
      
      return {
        lng: sumX / ring.length,
        lat: sumY / ring.length,
      };
    }
    
    // For ArcGIS point geometry
    if ('x' in geometry && 'y' in geometry) {
      return {
        lng: (geometry as any).x,
        lat: (geometry as any).y,
      };
    }
    
    // Default to Hanoi center
    return { lat: 21.028511, lng: 105.804817 };
  }

  /**
   * Get current position
   */
  private getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      });
    });
  }

  /**
   * Detect if mobile device
   */
  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }

  /**
   * Get travel time estimate (requires Google Directions API)
   */
  async getTravelTime(
    _origin: string | { lat: number; lng: number },
    _destination: string | { lat: number; lng: number },
    _travelMode: 'driving' | 'walking' | 'transit' | 'bicycling' = 'driving'
  ): Promise<{ duration: string; distance: string } | null> {
    // This would require Google Directions API key and backend service
    // For now, return null
    console.log('Travel time estimation requires Google Directions API');
    return null;
  }
}

// Export singleton instance
export const navigationService = NavigationService.getInstance();
